import React from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';

/**
 * Shows the onboarding progress + next action card.
 * Clicking the card (or "View full checklist") invokes onOpenChecklist(stepId).
 */
export default function CompleteYourSetupSection({ client, onOpenChecklist }) {
  const onboarding = client?.onboarding || {};
  const steps = [
    { id: 'contracts', order: 1, title: 'Contracts signed', actionTitle: 'Sign your contracts', description: 'Review and sign the HealthCues Master Services Agreement and Carrier Authorization forms.' },
    { id: 'banking', order: 2, title: 'Banking verified', actionTitle: 'Verify your banking', description: 'Connect your payroll account (via Plaid) or enter routing/account details manually.' },
    { id: 'census', order: 3, title: 'Census uploaded', actionTitle: 'Upload your employee census', description: 'Download the template, fill in your employee data, and upload to get your full analysis.' },
    { id: 'enrollment', order: 4, title: 'Employee enrollment', actionTitle: 'Launch employee enrollment', description: 'Invite your employees to review and enroll in their benefits.' },
    { id: 'goLive', order: 5, title: 'Go live', actionTitle: 'Go live', description: 'Activate the program and begin realizing savings on your next payroll.' },
  ];

  const statusOf = (id) => (onboarding.steps?.[id]?.status) || 'not_started';
  const stepsWithStatus = steps.map(s => ({ ...s, status: statusOf(s.id) }));
  const nextStep = stepsWithStatus.find(s => s.status !== 'complete');
  const completedCount = stepsWithStatus.filter(s => s.status === 'complete').length;
  const allComplete = completedCount === steps.length;
  const progressPct = Math.round((completedCount / steps.length) * 100);

  if (allComplete) {
    return (
      <div data-testid="setup-section" style={{ ...S.card, padding: 24, background: C.greenLight, border: `1px solid ${C.green}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="checkmark-circle" style={{ fontSize: 22, color: C.green }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: C.green }}>Onboarding complete! Your HealthCues program is active.</div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <QuickLink icon="document-text-outline" label="View contracts" onClick={() => onOpenChecklist('contracts')} />
          <QuickLink icon="card-outline" label="Manage banking" onClick={() => onOpenChecklist('banking')} />
          <QuickLink icon="people-outline" label="View census" onClick={() => onOpenChecklist('census')} />
          <QuickLink icon="checkmark-done-outline" label="Enrollment dashboard" onClick={() => onOpenChecklist('enrollment')} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="setup-section" style={{ ...S.card, padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <SectionHeader title="Complete Your Setup" icon="checkmark-done-outline" />
        <button
          type="button"
          data-testid="view-full-checklist-btn"
          onClick={() => onOpenChecklist(nextStep?.id || 'contracts')}
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.accent,
            background: 'transparent',
            border: `1px solid ${C.accent}`,
            padding: '6px 12px',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          View full checklist →
        </button>
      </div>

      {/* Progress bar + step dots */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 11, color: C.textLight, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {completedCount} of {steps.length} steps complete ({progressPct}%)
          </div>
        </div>
        <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${progressPct}%`, height: '100%', background: C.accent, transition: 'width 0.4s ease' }} />
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {stepsWithStatus.map(s => (
            <StepPill key={s.id} step={s} active={s.id === nextStep?.id} onClick={() => onOpenChecklist(s.id)} />
          ))}
        </div>
      </div>

      {/* Next action card */}
      {nextStep && (
        <button
          type="button"
          data-testid="next-action-card"
          onClick={() => onOpenChecklist(nextStep.id)}
          style={{
            width: '100%',
            textAlign: 'left',
            background: C.accentBg,
            border: `2px solid ${C.accent}`,
            borderRadius: 10,
            padding: '16px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
            {nextStep.order}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{nextStep.actionTitle}</div>
            <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.5 }}>{nextStep.description}</div>
          </div>
          <Icon name="arrow-forward" style={{ fontSize: 20, color: C.accent }} />
        </button>
      )}

      {/* Future steps (greyed out) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginTop: 14 }}>
        {stepsWithStatus.filter(s => s.id !== nextStep?.id && s.status !== 'complete').map(s => (
          <div
            key={s.id}
            style={{
              background: C.bg,
              border: `1px dashed ${C.border}`,
              borderRadius: 8,
              padding: '10px 12px',
              opacity: 0.7,
            }}
          >
            <div style={{ fontSize: 10, color: C.textLight, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Step {s.order}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.textMid, marginTop: 2 }}>{s.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionHeader({ title, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon name={icon} style={{ fontSize: 20, color: C.accent }} />
      <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{title}</h2>
    </div>
  );
}

function StepPill({ step, active, onClick }) {
  const isDone = step.status === 'complete';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`step-pill-${step.id}`}
      style={{
        padding: '4px 10px',
        borderRadius: 999,
        background: isDone ? C.greenLight : active ? C.accentBg : C.bg,
        border: `1px solid ${isDone ? C.green : active ? C.accent : C.border}`,
        color: isDone ? C.green : active ? C.accent : C.textMid,
        fontSize: 11,
        fontWeight: 700,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
      }}
    >
      <Icon name={isDone ? 'checkmark-circle' : active ? 'ellipse' : 'ellipse-outline'} style={{ fontSize: 11 }} />
      {step.title}
    </button>
  );
}

function QuickLink({ icon, label, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: C.text, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon name={icon} style={{ fontSize: 14, color: C.accent }} />
      {label}
    </button>
  );
}
